import type {
  AdministrationLegacyRoute,
  AdministrationLocation,
} from "@kontextwerk/control-web-frame/components/administration"
import type { OrgaViewFrameContext } from "../context"

/** Historical tab ordering is frozen independently of the new catalog. */
export const getLegacyAdministrationRoutes = (
  context: OrgaViewFrameContext,
  location?: AdministrationLocation,
): AdministrationLegacyRoute[] => {
  const allows = (
    resource: string,
    level: NonNullable<Parameters<OrgaViewFrameContext["permissions"]["allows"]>[1]>,
  ) => context.permissions.allows(resource, level)
  const settings = allows("core.org_settings", "view") || allows("core.org_settings", "manage")
  const restricted = context.subscriptionRestrictionActive && !context.permissions.isSuper
  const billing =
    context.billingAvailable &&
    context.billingEnabled &&
    (allows("core.billing", "view") || allows("core.billing", "manage")) &&
    (context.billingRequiresCheckout ||
      context.tariffCapabilities.hasUsage ||
      allows("core.billing", "manage"))
  const danger = Boolean(
    context.organization &&
    allows("core.org_settings", "manage") &&
    (context.permissions.isSuper || context.currentUserOrganizationId === context.organization.id),
  )
  const documents =
    context.billingAvailable &&
    (context.currentUserRole === "admin" || context.currentUserRole === "customer_admin") &&
    allows("core.billing", "view")
  const session =
    context.tariffCapabilities.hasSessionConfig &&
    (allows("core.session_details", "view") || allows("core.session_details", "manage"))
  const signature = new URLSearchParams(location?.search ?? "").get("signature_action") === "draw"
  const planningTarget = signature
    ? "administration/announcement-templates/letter/other"
    : "administration/planning"
  const managementTargets = [
    ...(settings ? ["administration/order-types"] : []),
    ...(!restricted && (allows("core.users", "view") || allows("core.users", "manage"))
      ? ["administration/users"]
      : []),
    ...(context.permissions.isSuper && context.tariffCapabilities.hasContactProfiles && settings
      ? ["administration/contact-profiles"]
      : []),
    ...(documents ? ["administration/legal-documents"] : []),
    ...(!restricted && context.tariffCapabilities.hasAiAgents && allows("core.projects", "view")
      ? ["administration/agents"]
      : []),
    ...(settings && context.tariffCapabilities.hasPhone ? ["administration/phone-numbers"] : []),
    ...(!restricted ? ["feedback"] : []),
  ]
  const settingsTargets = [
    ...(settings ? ["administration/organization/general", planningTarget] : []),
    ...(billing ? ["administration/billing"] : []),
    ...(allows("core.audit_log", "view") ? ["administration/audit"] : []),
    ...(danger ? ["administration/organization/danger"] : []),
  ]

  return [
    { path: "management", targets: managementTargets, defaultTarget: "administration" },
    { path: "organization", targets: settingsTargets },
    {
      path: "session-details",
      targets: session
        ? [
            "administration/ai-rules/answer-rules",
            "administration/ai-rules/human-handover",
            ...(context.permissions.isSuper ? ["administration/ai-rules/secrets"] : []),
          ]
        : [],
    },
    { path: "customers", targets: ["administration/customers", "administration/properties"] },
    { path: "users", targets: ["administration/users"] },
    { path: "billing", targets: ["administration/billing"] },
    {
      path: "administration/organization",
      targets: [
        ...(settings ? ["administration/organization/general"] : []),
        ...(danger ? ["administration/organization/danger"] : []),
      ],
    },
    ...(signature ? [{ path: "administration/planning", targets: [planningTarget] }] : []),
  ]
}

export const legacyAdministrationAliases = {
  "administration/district/customers": "administration/customers",
  "administration/district/addresses": "administration/properties",
}
